import { onBeforeUnmount, ref, type Ref } from 'vue';
import { useReviewWebSocket } from '@/composables/useReviewWebSocket';
import { logger } from '@/utils/logger';

/**
 * Trailing window over a burst of `REVIEW_UPDATE` events. Extractions finish in
 * clusters, so without this each finished cell would cost its own round trip.
 */
const COALESCE_MS = 400;

export interface UseReviewLiveReturn {
  /** True while a refetch is in flight — never bind this to a loading veil. */
  isRefetching: Ref<boolean>;
  /**
   * Ask for a refetch. Safe to call from anywhere and as often as you like:
   * calls inside the coalescing window collapse into one, and a call arriving
   * while a refetch is running sets a dirty flag instead of queueing.
   */
  scheduleRefetch: () => void;
}

/**
 * One handle over a review's live updates.
 *
 * The websocket payload is only a uid, so every event means "refetch" — the
 * caller passes the *narrowest* refetch that keeps its own view correct (the
 * insight grid reloads one `getReviewTableByUid`; the contract view reloads the
 * one file review it is showing). Pollers should call `scheduleRefetch` too, so
 * socket and poll share a single gate instead of racing each other.
 *
 * Deliberately does not expose a loading flag for the UI: a refresh triggered
 * by an event must not flash a spinner, or a processing review strobes.
 */
export function useReviewLive(
  reviewUid: string | undefined,
  refetch: () => Promise<void> | void
): UseReviewLiveReturn {
  const isRefetching = ref(false);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let dirty = false;

  async function run(): Promise<void> {
    if (isRefetching.value) {
      // Something changed again mid-flight; run once more when this one lands
      // rather than starting a second overlapping pass.
      dirty = true;
      return;
    }

    isRefetching.value = true;
    try {
      await refetch();
    } catch (error) {
      logger.error('[ReviewLive] Refetch failed:', error);
    } finally {
      isRefetching.value = false;
      if (dirty) {
        dirty = false;
        scheduleRefetch();
      }
    }
  }

  function scheduleRefetch(): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void run();
    }, COALESCE_MS);
  }

  useReviewWebSocket(reviewUid, () => {
    scheduleRefetch();
  });

  onBeforeUnmount(() => {
    if (timer) clearTimeout(timer);
    timer = null;
  });

  return { isRefetching, scheduleRefetch };
}
